// src/views/MintView.jsx - UPDATED SALE STATUS LOGIC
import React, { useState } from 'react';
import { ArrowRight, AlertCircle, CheckCircle, ExternalLink } from 'lucide-react';
import NFTSimulator from '../components/NFTSimulator/NFTSimulator';

export const MintView = ({ stats, saleActive, loading, mint, isConnected, connectWallet, nftBalance }) => {
  const [quantity, setQuantity] = useState(1);
  
  // Determine sale status
  const isSoldOut = stats.totalSupply >= 10000;
  const saleNotStarted = !saleActive && stats.totalSupply === 0;
  const salePaused = !saleActive && stats.totalSupply > 0 && !isSoldOut;
  
  return (
    <div className="space-y-12">
      {/* NFT Simulator Section */}
      <div>
        <h2 className="text-3xl md:text-5xl font-black mb-8 text-white font-mono uppercase tracking-tight">
          EXPERIENCE THE ART
        </h2>
        <NFTSimulator />
      </div>
      
      {/* Mint Section */}
      <div>
        <h2 className="text-3xl md:text-5xl font-black mb-8 text-white font-mono uppercase tracking-tight">
          MINT YOUR DUNK
        </h2>
        
        {/* Sale Status Warnings */}
        {isSoldOut && (
          <div className="bg-yellow-400 text-black p-6 border-4 border-black flex items-start gap-4 mb-8 font-mono shadow-[8px_8px_0_0_#000]">
            <AlertCircle size={32} className="flex-shrink-0" />
            <div className="flex-1">
              <div className="font-black text-2xl mb-3 uppercase">
                🎉 SOLD OUT
              </div>
              <div className="text-base mb-4 font-bold">
                All 10,000 Dunk Poge NFTs have been minted. Find them on secondary markets:
              </div>
              <div className="flex flex-wrap gap-3">
                <a 
                  href="https://opensea.io/collection/dunkpoge" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-black text-white px-6 py-3 border-4 border-black font-black uppercase flex items-center gap-2 hover:bg-gray-900 transition-all shadow-[4px_4px_0_0_#fff] hover:shadow-[6px_6px_0_0_#fff] hover:-translate-x-0.5 hover:-translate-y-0.5"
                >
                  OpenSea <ExternalLink size={16} />
                </a>
                <a 
                  href="https://blur.io/collection/dunkpoge" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-black text-white px-6 py-3 border-4 border-black font-black uppercase flex items-center gap-2 hover:bg-gray-900 transition-all shadow-[4px_4px_0_0_#fff] hover:shadow-[6px_6px_0_0_#fff] hover:-translate-x-0.5 hover:-translate-y-0.5"
                >
                  Blur <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </div>
        )}

        {saleNotStarted && (
          <div className="bg-blue-500 text-white p-6 border-4 border-white flex items-start gap-4 mb-8 font-mono shadow-[8px_8px_0_0_#00ff00]">
            <AlertCircle size={32} className="flex-shrink-0" />
            <div>
              <div className="font-black text-2xl mb-2 uppercase">
                ⏳ SALE NOT STARTED
              </div>
              <div className="text-base opacity-90">
                Minting has not begun yet. Check back soon or follow our announcements.
              </div>
            </div>
          </div>
        )}

        {salePaused && (
          <div className="bg-orange-500 text-white p-6 border-4 border-white flex items-start gap-4 mb-8 font-mono shadow-[8px_8px_0_0_#ff0066]">
            <AlertCircle size={32} className="flex-shrink-0" />
            <div>
              <div className="font-black text-2xl mb-2 uppercase">
                ⏸️ SALE PAUSED
              </div>
              <div className="text-base opacity-90">
                Minting is temporarily disabled. {stats.totalSupply}/10,000 have been minted. Check back later.
              </div>
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* Mint Info Card */}
          <div className="bg-white text-black p-8 border-4 border-green-400 relative font-mono shadow-[12px_12px_0_0_#00ff00]">
            <h3 className="text-3xl font-black mb-6 uppercase">
              MINT INFO
            </h3>
            
            {/* Stats Grid */}
            <div className="mb-8 space-y-4">
              <div className="flex justify-between pb-2 border-b-2 border-black">
                <span className="font-bold">STATUS:</span>
                <span className={`text-xl font-black uppercase ${
                  isSoldOut ? 'text-yellow-600' : 
                  saleActive ? 'text-green-500' : 
                  'text-red-500'
                }`}>
                  {isSoldOut ? '🎉 SOLD OUT' : saleActive ? '✓ ACTIVE' : '✗ INACTIVE'}
                </span>
              </div>
              
              <div className="flex justify-between pb-2 border-b-2 border-black">
                <span className="font-bold">PRICE:</span>
                <span className="text-2xl font-black">{stats.price} ETH</span>
              </div>
              
              <div className="flex justify-between pb-2 border-b-2 border-black">
                <span className="font-bold">SUPPLY:</span>
                <span className="text-2xl font-black">{stats.totalSupply}/10,000</span>
              </div>
              
              <div className="flex justify-between pb-2 border-b-2 border-black">
                <span className="font-bold">YOUR NFTs:</span>
                <span className="text-2xl font-black">{nftBalance}</span>
              </div>
            </div>
            
            {/* Quantity Selector - Hidden if sold out */}
            {!isSoldOut && (
              <>
                <div className="mb-6">
                  <label className="block font-black mb-3 text-lg uppercase">
                    QUANTITY:
                  </label>
                  <input 
                    type="number" 
                    min="1" 
                    max="10"
                    value={quantity} 
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value > 0) {
                        setQuantity(Math.min(10, Math.max(1, value)));
                      } else {
                        setQuantity(1);
                      }
                    }}
                    className="w-full p-4 font-black text-2xl border-4 border-black bg-white text-black text-center mb-2 font-mono"
                    disabled={!saleActive}
                  />
                  <div className="font-black text-center text-gray-600">
                    TOTAL: {(parseFloat(stats.price) * quantity).toFixed(3)} ETH
                  </div>
                </div>
                
                {/* Mint Button */}
                <button 
                  onClick={() => mint(quantity)} 
                  disabled={loading || !isConnected || !saleActive}
                  className={`w-full font-black text-xl py-5 border-4 border-white flex items-center justify-center gap-3 uppercase font-mono transition-all duration-200 ${
                    loading || !isConnected || !saleActive
                      ? 'bg-gray-600 cursor-not-allowed opacity-70'
                      : 'bg-blue-600 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[10px_10px_0_0_#000]'
                  } shadow-[8px_8px_0_0_#000] text-white`}
                >
                  {loading ? 'PROCESSING...' : !isConnected ? 'CONNECT WALLET' : !saleActive ? 'SALE NOT ACTIVE' : 'MINT NOW'}
                  {!loading && saleActive && isConnected && <ArrowRight size={24} />}
                </button>
              </>
            )}

            {/* Sold Out CTA */}
            {isSoldOut && (
              <div className="space-y-4">
                <div className="bg-yellow-400 text-black p-4 border-4 border-black text-center font-black text-lg uppercase">
                  PRIMARY SALE COMPLETE
                </div>
                <a 
                  href="https://opensea.io/collection/dunkpoge"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-black text-white font-black text-xl py-5 border-4 border-black flex items-center justify-center gap-3 uppercase font-mono transition-all duration-200 hover:bg-gray-900 shadow-[8px_8px_0_0_#00ff00] hover:shadow-[10px_10px_0_0_#00ff00] hover:-translate-x-0.5 hover:-translate-y-0.5"
                >
                  VIEW ON OPENSEA
                  <ExternalLink size={24} />
                </a>
              </div>
            )}
            
            {/* On-Chain Badge */}
            <div className="absolute -bottom-5 -right-5 bg-green-400 text-black px-6 py-3 font-black border-4 border-black rotate-3 shadow-[6px_6px_0_0_#000] text-sm uppercase font-mono tracking-wide z-10">
              VERY ON-CHAIN
            </div>
          </div>

{/* Info Card */}
<div className="bg-blue-600 text-white p-8 border-4 border-white relative font-mono shadow-[0_0_30px_rgba(0,255,0,0.3),8px_8px_0_0_#fff]">
  {/* Info Badge */}
  <div className="absolute -top-5 left-6 bg-green-400 text-black px-4 py-2 font-black border-4 border-black shadow-[4px_4px_0_0_#000] text-sm uppercase font-mono">
    TRUSTLESS BY DESIGN
  </div>
  
  <ul className="mt-4 space-y-4">
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>10,000 immutable Dunks.</strong> On-chain forever.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>2 billion combos</strong> from single contract. No admin keys.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>SVG code, not JPEG files.</strong> Resolution infinite.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Zero external dependencies.</strong> No servers to fail.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Free public simulator.</strong> Experience before ownership.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Stake for governance.</strong> Utility without subscription.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Cultural remix.</strong> CryptoPunk meets Doge spirit.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>One contract.</strong> Minimal trust surface area.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Art is free.</strong> Provenance costs ETH.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Permanence guaranteed by Ethereum,</strong> not promises.
      </span>
    </li>
  </ul>
  
  {/* Decorative Element */}
  <div className="absolute bottom-4 right-4 text-xs opacity-50 italic uppercase tracking-widest">
    Such Art • Much Wow
  </div>
</div>
        </div>
      </div>
    </div>
  );
};